import { useQuery } from '@tanstack/react-query'
import { useMemo } from 'react'
import { Link } from 'react-router-dom'
import { fetchArtists } from '../api/public'
import { useCrtPageTitle } from '../hooks/useCrtPageTitle'
import { usePageMeta } from '../hooks/usePageMeta'
import { cmsImageUrl } from '../lib/cms-image'
import { schemaArtistList, schemaBreadcrumb, schemaGraph, schemaWebPage } from '../lib/schema-org'

export function ArtistsPage() {
  useCrtPageTitle('Artists')
  const q = useQuery({
    queryKey: ['artists'],
    queryFn: fetchArtists,
  })

  const artists = q.data?.artists ?? []
  const jsonLd = useMemo(() => {
    const crumbs = schemaBreadcrumb(
      [
        { name: 'Home', path: '/' },
        { name: 'Artists', path: '/artists' },
      ],
      '/artists',
    )
    return schemaGraph(
      crumbs,
      schemaWebPage({
        path: '/artists',
        name: 'Artists — BNKR',
        description: 'BNKR Familia — DJs und Acts aus der Ostschweiz.',
        pageType: 'CollectionPage',
        breadcrumb: crumbs,
        mainEntity: schemaArtistList(artists),
      }),
      schemaArtistList(artists),
    )
  }, [artists])

  usePageMeta({
    title: 'Artists',
    description: 'Alle BNKR Artists — DJs und Acts zwischen Daydance, Techno und House.',
    canonical: '/artists',
    jsonLd,
  })

  return (
    <section className="section" style={{ paddingBottom: 'var(--sp-16)', paddingTop: 'var(--sp-4)' }}>
      <div className="wrap">
        <div className="sect-head">
          <div>
            <div className="kicker crt">↳ /artists</div>
            <h1 className="display h2" style={{ marginTop: 14 }}>
              Artists
            </h1>
          </div>
          <div className="lead" style={{ fontSize: 18, maxWidth: '50ch' }}>
            BNKR Familia — Acts zwischen Daydance, Techno, House und allem dazwischen.
          </div>
        </div>

        {q.isLoading && (
          <p className="muted" style={{ fontFamily: 'var(--mono)', letterSpacing: '.14em', fontSize: 13 }}>
            LOADING…
          </p>
        )}
        {q.isError && <p className="error">Artists konnten nicht geladen werden.</p>}
        {q.data && q.data.artists.length === 0 && (
          <p className="muted">Noch keine Artists.</p>
        )}
        <div className="artist-grid">
          {q.data?.artists.map((a) => (
            <Link key={a.id} to={`/artists/${a.slug}`} className="artist" aria-label={a.name}>
              <div
                className="ph"
                style={
                  a.portraitImageUrl
                    ? {
                        backgroundImage: `url(${cmsImageUrl(a.portraitImageUrl, 'thumb') ?? a.portraitImageUrl})`,
                        backgroundSize: 'cover',
                        backgroundPosition: 'center',
                      }
                    : undefined
                }
              />
              <div className="label" aria-hidden>
                <span>{a.name}</span>
                <span className="tag">CH</span>
              </div>
            </Link>
          ))}
        </div>
      </div>
    </section>
  )
}
